import styled, { css } from 'styled-components';

import { media } from '@savgroup-front-common/constants';
import { rgba } from '@savgroup-front-common/primitive/helpers/cssColor';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import {
  SUPPORTED_COMPONENTS,
  SUPPORTED_PROPERTIES,
} from '@savgroup-front-common/types';

import { Button } from '../../../../atoms/button';
import { styledGetCustomComponentProperty } from '../../../../helpers';

import { SearchInputVariant } from './DataTableSearchInput.types';

export const $SearchIconContainer = styled.div`
  position: absolute;
  left: ${spacing('md')};
  height: 100%;
  display: flex;
  align-items: center;
`;
export const $SearchLoadingContainer = styled.div`
  position: absolute;
  top: ${spacing(2.5)};
  left: ${spacing('xxs')};
  background: #eee;
  border-radius: 50%;
  width: ${spacing('lg')};
  height: ${spacing('lg')};
`;

export const $SearchInputContainer = styled.div<{
  $componentThemeName?: string;
  $variant: SearchInputVariant;
}>`
  max-width: ${({ $componentThemeName }) =>
    $componentThemeName
      ? styledGetCustomComponentProperty({
          componentName: $componentThemeName,
          property: SUPPORTED_PROPERTIES.MAX_WIDTH,
        })
      : null};
  position: relative;
  display: flex;
  width: 100%;

  height: ${({ $variant, theme }) =>
    $variant === SearchInputVariant.COMPACT_DEFAULT
      ? theme.systemColors.defaultBackground
      : styledGetCustomComponentProperty({
          componentName: SUPPORTED_COMPONENTS.DEFAULT_INPUTS,
          property: SUPPORTED_PROPERTIES.HEIGHT,
        })};
`;

export const $SearchClearButton = styled(Button)`
  opacity: 0;
  pointer-events: none;
  transition-duration: 0.3s;

  &:focus {
    opacity: 1;
    pointer-events: all;
  }
`;
export const $SearchActionContainer = styled.div`
  position: absolute;
  right: 0;

  display: flex;
  flex-direction: row;
  width: fit-content;
  button {
    max-width: ${styledGetCustomComponentProperty({
      componentName: SUPPORTED_COMPONENTS.DEFAULT_INPUTS,
      property: SUPPORTED_PROPERTIES.HEIGHT,
    })};
  }
`;

const defaultVariant = css<{ $variant: SearchInputVariant }>`
  background: ${({ $variant, theme }) =>
    $variant === SearchInputVariant.DEFAULT
      ? theme.systemColors.defaultBackground
      : null};
  border-color: ${({ $variant, theme }) =>
    $variant === SearchInputVariant.DEFAULT ? theme.colors.default : null};

  &:focus {
    border-color: ${({ $variant, theme }) =>
      $variant === SearchInputVariant.DEFAULT ? theme.colors.primary : null};
  }
  &:hover {
    border-color: ${({ $variant, theme }) =>
      $variant === SearchInputVariant.DEFAULT ? theme.colors.primary : null};
    background: ${({ $variant, theme }) =>
      $variant === SearchInputVariant.DEFAULT
        ? theme.systemColors.defaultBackground
        : null};
    box-shadow: ${({ theme }) =>
      `0px 0px 4px ${rgba(theme.colors.primary, 0.4)}`};
  }
`;

const compactDefaultVariant = css<{ $variant: SearchInputVariant }>`
  background: ${({ $variant, theme }) =>
    $variant === SearchInputVariant.COMPACT_DEFAULT
      ? theme.systemColors.defaultBackground
      : null};
  border-color: ${({ $variant, theme }) =>
    $variant === SearchInputVariant.COMPACT_DEFAULT
      ? theme.colors.default
      : null};

  &:focus {
    border-color: ${({ $variant, theme }) =>
      $variant === SearchInputVariant.COMPACT_DEFAULT
        ? theme.colors.primary
        : null};
  }
  &:hover {
    border-color: ${({ $variant, theme }) =>
      $variant === SearchInputVariant.COMPACT_DEFAULT
        ? theme.colors.primary
        : null};
    background: ${({ $variant, theme }) =>
      $variant === SearchInputVariant.COMPACT_DEFAULT
        ? theme.systemColors.defaultBackground
        : null};
    box-shadow: ${({ theme }) =>
      `0px 0px 4px ${rgba(theme.colors.primary, 0.4)}`};
  }

  & {
    height: ${({ $variant }) =>
      $variant === SearchInputVariant.COMPACT_DEFAULT ? '32px' : null};
  }
`;

const globalSearchVariant = css<{ $variant: SearchInputVariant }>`
  background: ${({ $variant, theme }) =>
    $variant === SearchInputVariant.GLOBAL_SEARCH
      ? theme.systemColors.defaultBackground
      : null};
  border-color: ${({ $variant }) =>
    $variant === SearchInputVariant.GLOBAL_SEARCH ? '#eee' : null};

  &:focus {
    border-color: ${({ $variant }) =>
      $variant === SearchInputVariant.GLOBAL_SEARCH ? '#999 !important' : null};
  }
  &:hover {
    border-color: ${({ $variant }) =>
      $variant === SearchInputVariant.GLOBAL_SEARCH ? '#e2e2e2' : null};
    background: ${({ $variant, theme }) =>
      $variant === SearchInputVariant.GLOBAL_SEARCH
        ? theme.systemColors.defaultBackground
        : null};
  }
`;

export const $SearchInput = styled.input<{ $variant: SearchInputVariant }>`
  height: ${styledGetCustomComponentProperty({
    componentName: SUPPORTED_COMPONENTS.DEFAULT_INPUTS,
    property: SUPPORTED_PROPERTIES.HEIGHT,
  })};
  &:not([value='']) {
    ~ * ${$SearchClearButton}, ~ ${$SearchClearButton} {
      opacity: 1;
      pointer-events: all;
    }
  }
  &[value=''] {
    ~ * ${$SearchClearButton}, ~ ${$SearchClearButton} {
      pointer-events: none;
    }
  }

  color: ${({ theme }) => theme.colors.mainTextColor};

  ${defaultVariant};
  ${globalSearchVariant};
  ${compactDefaultVariant};

  border-radius: ${({ theme }) => theme.tw.radius.sm};
  padding: 0 ${spacing('sm')} 0 ${spacing(12)};
  flex: 1;

  transition-duration: 0.3s;
  border-width: 1px;
  border-style: solid;

  &:hover,
  &:focus {
    outline: 0;
  }

  @media ${media.maxWidth.xs} {
    padding: 0 ${spacing(10)} 0 ${spacing(12)};
  }
`;
